import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CiFileOn } from "react-icons/ci";
import { Post } from "../../types/Types";
import { formatDMYHM, formatTimeAgo } from "../../utils/DateUtils";
import AudioPlayer from "./AudioPlayer";

// Card for the Home and Projects pages. (Profile keeps using <Card />.)
// Everything shown here comes from the post the projects list already
// returns: owner, collaboration sections with their audio files, and the
// sheet music file.

interface ProjectCardProps {
  post: Post;
  onAudioPlay: (audio: HTMLAudioElement) => void;
  variant?: "grid" | "list";
}

const MAX_AVATARS = 3;

export const audioCountOf = (post: Post) =>
  (post.sections ?? []).reduce((n, s) => n + (s.audio_files?.length ?? 0), 0);

// Musicians who added audio to the project, once each.
const contributorsOf = (post: Post) => {
  const seen = new Map<string, { id: string; username: string; photo: string | null }>();
  (post.sections ?? []).forEach((section) =>
    (section.audio_files ?? []).forEach((audio) => {
      const id = String(audio.user);
      if (!seen.has(id)) {
        seen.set(id, { id, username: audio.user_info?.username ?? "?", photo: audio.user_info?.profile_photo ?? null });
      }
    })
  );
  return Array.from(seen.values());
};

const Avatar: React.FC<{ name: string; photo?: string | null; className?: string }> = ({ name, photo, className = "" }) =>
  photo ? (
    <img src={photo} alt="" className={`w-7 h-7 rounded-full object-cover border-2 border-white bg-white shrink-0 ${className}`} />
  ) : (
    <span
      aria-hidden="true"
      className={`w-7 h-7 rounded-full border-2 border-white bg-secondary text-primary grid place-items-center text-[11px] font-bold shrink-0 ${className}`}
    >
      {name.charAt(0).toUpperCase()}
    </span>
  );

const ProjectCard: React.FC<ProjectCardProps> = ({ post, onAudioPlay, variant = "grid" }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const handleAudioPlay = () => {
    setIsPlaying(true);
    if (audioRef.current) onAudioPlay(audioRef.current);
  };

  const sections = post.sections ?? [];
  const contributors = contributorsOf(post);
  const isList = variant === "list";

  const owner = post.user_info && (
    // min width so the name never gets squeezed away; the badges wrap below instead
    <div className="flex flex-1 items-center gap-2 min-w-[11rem] text-[13px] text-gray-70 whitespace-nowrap">
      {/* Above the card-wide link, so it opens the owner's profile instead. */}
      <Link to={`/profile/${post.user}`} className="relative z-10 flex items-center gap-2 min-w-0 hover:underline">
        <Avatar name={post.user_info.username} photo={post.user_info.profile_photo} className="!border-0" />
        <span className="font-semibold text-gray-800 truncate">{post.user_info.username}</span>
      </Link>
      {post.created_at && <span title={formatDMYHM(post.created_at)}>· {formatTimeAgo(post.created_at)}</span>}
    </div>
  );

  const badges = (post.pdf_file || post.is_archived) && (
    <div className="flex gap-1.5 shrink-0">
      {post.pdf_file && (
        <span className="flex items-center gap-1 text-[11px] font-bold rounded-full px-2 py-0.5 bg-gray-f6 text-black41">
          <CiFileOn className="text-sm" /> Sheet music
        </span>
      )}
      {post.is_archived && (
        <span className="text-[11px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 bg-gray-200 text-black41">Archived</span>
      )}
    </div>
  );

  const title = (
    <div className="flex items-center gap-2 min-w-0">
      {isPlaying && (
        <span className="flex items-end gap-[2px] h-3.5 shrink-0" aria-hidden="true">
          {[0, -0.3, -0.6].map((delay) => (
            <span key={delay} className="w-[3px] h-[40%] rounded-[1px] bg-primary animate-equalizer" style={{ animationDelay: `${delay}s` }} />
          ))}
        </span>
      )}
      {/* The player sits outside this link (so seeking never opens the
          project); the link's ::after covers the rest of the card instead. */}
      <Link
        to={`/post-details/${post.id}`}
        className="min-w-0 after:absolute after:inset-0 after:rounded-3xl focus:outline-none focus-visible:underline"
      >
        <h2 className={`font-semibold text-gray-800 ${isList ? "text-base truncate" : "text-lg leading-snug line-clamp-2"}`}>{post.title}</h2>
      </Link>
    </div>
  );

  const sectionChips =
    sections.length > 0 ? (
      <div className="flex flex-wrap gap-1.5">
        {sections.map((section) => {
          const n = section.audio_files?.length ?? 0;
          return (
            <span
              key={section.id}
              className={`text-xs font-semibold text-primary px-2.5 py-0.5 rounded-full whitespace-nowrap ${
                n === 0 ? "ring-[1.5px] ring-inset ring-primary/25" : "bg-primary/[0.07]"
              }`}
            >
              {section.instrument.name}{" "}
              <span className="font-bold opacity-75">· {n === 0 ? "no audio yet" : `${n} ${n === 1 ? "audio" : "audios"}`}</span>
            </span>
          );
        })}
      </div>
    ) : post.instruments?.length ? (
      <div className="flex flex-wrap gap-1.5">
        {post.instruments.map((instrument) => (
          <span key={instrument.id} className="text-xs font-semibold text-primary bg-primary/[0.07] px-2.5 py-0.5 rounded-full">
            {instrument.name}
          </span>
        ))}
      </div>
    ) : null;

  const joined =
    contributors.length > 0 ? (
      <div className="flex items-center gap-2.5 text-[13px] text-black41">
        <span className="flex">
          {contributors.slice(0, MAX_AVATARS).map((c, i) => (
            <Avatar key={c.id} name={c.username} photo={c.photo} className={i > 0 ? "-ml-2" : ""} />
          ))}
          {contributors.length > MAX_AVATARS && (
            <span className="-ml-2 w-7 h-7 rounded-full border-2 border-white bg-white text-black41 grid place-items-center text-[11px] font-bold">
              +{contributors.length - MAX_AVATARS}
            </span>
          )}
        </span>
        {isList
          ? `${contributors.length} joined`
          : `${contributors.length} ${contributors.length === 1 ? "musician has" : "musicians have"} joined`}
      </div>
    ) : post.is_archived ? null : (
      <p className="text-[13px] text-gray-70">{isList ? "No one yet" : "No one has joined yet. Be the first!"}</p>
    );

  const player = (
    <AudioPlayer
      className={`w-full relative z-10 ${isList ? "" : "mt-1"}`}
      ref={audioRef}
      waveformSeed={String(post.id)}
      onPlay={handleAudioPlay}
      onPause={() => setIsPlaying(false)}
      onEnded={() => setIsPlaying(false)}
    >
      <source src={post.mp3_file} type="audio/mpeg" />
      Your browser does not support the audio element.
    </AudioPlayer>
  );

  const frame = `relative bg-white transition-shadow duration-300 ${
    isPlaying ? "shadow-lg ring-2 ring-primary/40" : "shadow-md hover:shadow-lg"
  }`;

  if (isList) {
    return (
      <div className={`${frame} rounded-2xl px-4 py-3 grid gap-3 items-center 1020:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,0.8fr)_minmax(0,1.4fr)]`}>
        <div className="flex flex-col gap-1 min-w-0">
          {title}
          <div className="flex flex-wrap items-center gap-2">
            {owner}
            {badges}
          </div>
        </div>
        <div className="min-w-0">{sectionChips}</div>
        <div className="min-w-0">{joined}</div>
        <div className="min-w-0">{player}</div>
      </div>
    );
  }

  return (
    <div className={`${frame} rounded-3xl py-5 px-5 768:px-6 flex flex-col gap-3 h-full min-w-0`}>
      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1.5">
        {owner}
        {badges}
      </div>
      {title}
      {post.description && <p className="text-gray-600 text-[15px] line-clamp-3">{post.description}</p>}
      {sectionChips}
      <div className="mt-auto flex flex-col gap-3 pt-1">
        {joined}
        {player}
      </div>
    </div>
  );
};

export default ProjectCard;
